import React from 'react';
import { compose } from 'redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import classnames from 'classnames';
import PropTypes from 'prop-types';
// Core components
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
import Tooltip from 'components/core/Tooltip';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import { title, priority } from 'models/scenario';
import {
  icon as deviceIcon,
  installationDesc as deviceInstallationDesc,
} from 'models/device';
// Custom
import { listRowSpecsShape, scenarioDataShape, deviceDataShape } from 'constants/PropTypesShapes';
import { scenariosShowPath } from 'installations/navigation';
import withDeviceData from 'appAssets/hocs/WithDeviceData';
import AppIcon from 'components/icons/AppIcon';
import { EditIconButton, DeleteIconButton } from 'components/buttons/IconButtons';
import ResourceAsyncStatusIcon from 'components/items/ResourceAsyncStatusIcon';
import TruncatedTooltipText from 'components/misc/TruncatedTooltipText';
import InfoCheckbox from 'components/misc/InfoCheckbox';
import ScenarioTriggerButton from './ScenarioTriggerButton';

const ScenarioPriority = ({ scenario }) => {
  const { labelId, icon, color } = priority(scenario);
  return (
    <div className="d-flex align-items-center">
      <Tooltip content={<LocalizedMessage id={labelId} />}>
        <AppIcon color={color} id={icon} />
      </Tooltip>
    </div>
  );
};

ScenarioPriority.propTypes = {
  scenario: scenarioDataShape.isRequired,
};

const ScenarioRow = React.forwardRef(({
  id, scenario, deviceData, rowSpecs,
  onTrigger, onDelete,
}, ref) => {
  const { id: scenarioId } = scenario;

  // Handlers
  const handleDelete = () => {
    onDelete(scenario);
  };

  return (
    <TableRow
      ref={ref}
      element={scenario}
      rowSpecs={rowSpecs}
      actions={[
        <OnlyWith
          key="bActions"
          permissions={{
            needed: permissions.pInstallation.configWrite,
            permissionsOrContext: 'installationPermissions',
          }}
        >
          <React.Fragment>
            <EditIconButton path={scenariosShowPath(id, scenarioId)} />
            <DeleteIconButton onClick={handleDelete} />
          </React.Fragment>
        </OnlyWith>,
      ]}
    >
      <TableCell className={classnames({ 'table-first-details': rowSpecs.withDetails })}>
        <TruncatedTooltipText>{title(scenario)}</TruncatedTooltipText>
      </TableCell>
      <TableCell className="hidden-xs-down">
        {deviceData && (
          <div className="d-flex align-items-center">
            <AppIcon defaultColor id={deviceIcon} className="mr-2" />
            <TruncatedTooltipText>{deviceInstallationDesc(deviceData)}</TruncatedTooltipText>
          </div>
        )}
      </TableCell>
      <TableCell className="table-content-1 table-content-center hidden-xs-down">
        <InfoCheckbox value={scenario.involved_in_antipassback} />
      </TableCell>
      <TableCell className="table-content-1 table-content-center hidden-xs-down">
        <ScenarioPriority scenario={scenario} />
      </TableCell>
      <TableCell className="table-content-1 table-content-center">
        <div className="d-flex align-items-center">
          <ResourceAsyncStatusIcon resource={scenario} idKey="idScenario" />
        </div>
      </TableCell>
      <OnlyWith
        permissions={{
          needed: permissions.pInstallation.scenarioTrigger,
          permissionsOrContext: 'installationPermissions',
        }}
      >
        <TableCell className="table-content-1 hidden-xs-down" noPadding>
          <ScenarioTriggerButton type="icon" id={scenarioId} onTrigger={() => onTrigger(scenario)} />
        </TableCell>
      </OnlyWith>
    </TableRow>
  );
});

ScenarioRow.displayName = 'ScenarioRow';

ScenarioRow.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  scenario: scenarioDataShape.isRequired,
  deviceData: deviceDataShape,
  rowSpecs: listRowSpecsShape.isRequired,
  onTrigger: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

ScenarioRow.defaultProps = {
  deviceData: null,
};

module.exports = compose(
  withDeviceData('scenario.device_id'),
)(ScenarioRow);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/scenarios/ScenarioRow.js